/**
 * eccp-promo.js
 * Auto-adds free side table(s) to cart based on recliner quantity.
 *
 * Rules:
 *   >= 2 recliners → 1 side table in cart
 *   >= 4 recliners → 2 side tables in cart
 *   < 2 recliners → no action (BXGY discount handles pricing)
 */

// ─── CONFIG ──────────────────────────────────────────────────────────────────
const SIDE_TABLE_HANDLE = 'bad-ass-recliners-whiskey-barrel-top-side-table';
const RECLINER_TAGS     = ['hide-leather', 'plush-fabric', 'italian-leather'];
const MAX_FREE_TABLES   = 2;
// ─────────────────────────────────────────────────────────────────────────────

let sideTableVariantId = null;
let isRunning          = false;
let suppressIntercept  = false;

// Cache: product_id → true/false (is it a recliner). Tags don't change per session.
const reclinerCache = new Map();

async function getSideTableVariantId() {
  if (sideTableVariantId) return sideTableVariantId;
  const res = await fetch(`/products/${SIDE_TABLE_HANDLE}.js`);
  if (!res.ok) {
    console.error('[eccp-promo] ❌ Could not fetch side table product:', SIDE_TABLE_HANDLE, res.status);
    return null;
  }
  const data = await res.json();
  sideTableVariantId = data.variants[0].id;
  return sideTableVariantId;
}

async function isReclinerProduct(item) {
  if (reclinerCache.has(item.product_id)) {
    return reclinerCache.get(item.product_id);
  }
  const product = await fetch(`/products/${item.handle}.js`)
    .then(r => r.ok ? r.json() : null)
    .catch(() => null);

  const tags = product?.tags || [];
  const isRecliner = RECLINER_TAGS.some(tag => tags.includes(tag));
  reclinerCache.set(item.product_id, isRecliner);
  return isRecliner;
}

async function countReclinerQty(cart, excludeVariantId) {
  // Resolve recliner status for all unique products in parallel (cache makes repeats free)
  const checks = cart.items
    .filter(item => item.variant_id !== excludeVariantId)
    .map(async item => ({ item, isRecliner: await isReclinerProduct(item) }));

  const results = await Promise.all(checks);

  let reclinerQty = 0;
  for (const { item, isRecliner } of results) {
    if (isRecliner) reclinerQty += item.quantity;
  }
  return reclinerQty;
}

function getSideTableQty(cart, variantId) {
  const item = cart.items.find(i => i.variant_id === variantId);
  return item ? item.quantity : 0;
}

function calcTargetQty(reclinerQty) {
  if (reclinerQty >= 4) return Math.min(2, MAX_FREE_TABLES);
  if (reclinerQty >= 2) return Math.min(1, MAX_FREE_TABLES);
  return 0;
}

function refreshCartDrawer() {
  document.dispatchEvent(new CustomEvent('cart:refresh', {
    bubbles: true,
    detail: { source: 'eccp-promo' }
  }));
}

async function addSideTables(variantId, qty) {
  suppressIntercept = true;
  try {
    for (let i = 0; i < qty; i++) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });
      if (!res.ok) {
        console.error('[eccp-promo] ❌ Failed to add side table:', await res.json().catch(() => null));
        return;
      }
    }
  } finally {
    suppressIntercept = false;
  }

  // /cart/add.js has already resolved, so the cart is updated server-side.
  // Refresh the drawer immediately — no artificial wait needed.
  refreshCartDrawer();
}

async function syncSideTable() {
  if (isRunning) return;
  isRunning = true;

  try {
    // Resolve variant ID and cart concurrently
    const [variantId, cart] = await Promise.all([
      getSideTableVariantId(),
      fetch('/cart.js').then(r => r.json())
    ]);
    if (!variantId) return;

    const reclinerQty = await countReclinerQty(cart, variantId);
    const currentQty  = getSideTableQty(cart, variantId);
    const targetQty   = calcTargetQty(reclinerQty);

    if (targetQty > currentQty) {
      await addSideTables(variantId, targetQty - currentQty);
    }
  } catch (err) {
    console.error('[eccp-promo] Error:', err);
  } finally {
    isRunning = false;
  }
}

// ─── FETCH INTERCEPTION (trigger) ────────────────────────────────────────────
const _originalFetch = window.fetch;
window.fetch = async function (...args) {
  const response = await _originalFetch.apply(this, args);
  try {
    const input = args[0];
    const url = (typeof input === 'string') ? input : (input && input.url) || '';
    if (/\/cart\/(add|change|update)/.test(url) && !suppressIntercept) {
      // Fire on the next tick — add has already resolved server-side
      Promise.resolve().then(syncSideTable);
    }
  } catch (e) { /* never break original fetch */ }
  return response;
};

// ─── INIT ────────────────────────────────────────────────────────────────────
window.addEventListener('load', () => {
  getSideTableVariantId();          // pre-warm variant ID so first add is instant
  setTimeout(syncSideTable, 500);   // catch existing cart (returning customer)
});